// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmailLine from "@store/Library/Components/Toolkit/Icons/Email/EmailLine";
import PhoneLine from "@store/Library/Components/Toolkit/Icons/Phone/PhoneLine";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { boxStyles } from "@store/Themes/common/mixins/box";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

const ContactUsSalesRepInfos = getWidgetComponent("ContactUs/ContactUsSalesRepInfos");

const enum fields {
    accountsReceivableEmail = "accountsReceivableEmail",
    description = "description",
    email = "email",
    phoneNumber = "phoneNumber",
}

export interface ContactUsHelpProps extends WidgetProps {
    fields: {
        accountsReceivableEmail?: string;
        description?: string;
        email?: string;
        phoneNumber?: string;
    };
}

export interface ContactUsHelpStyles {
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    emailText?: TypographyPresentationProps;
    phoneText?: TypographyPresentationProps;
    emailIcon?: IconPresentationProps;
    phoneIcon?: IconPresentationProps;
}

const infoTextCss = css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: ${pxToRem(8)};
`;

const iconCss = css`
    margin-right: ${pxToRem(4)};
`;

const contactUsHelpStyles: ContactUsHelpStyles = {
    wrapper: {
        css: css`
            ${boxStyles(pxToRemArray([24]), true)};
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(24)};

            ${breakpoint("sm", "only")} {
                flex-direction: row;
                justify-content: space-between;
            }
        `,
    },
    infoWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            padding: ${pxToRem(24)} 0 0;

            ${breakpoint("sm", "only")} {
                display: flex;
                flex-direction: column;
                gap: ${pxToRem(8)};
                padding: 0;
                padding-left: ${pxToRem(24)};
                border-top: none;
                border-left: 1px solid ${getColor("common.border")};
                width: 50%;
            }
        `,
    },
    title: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: 0;
        `,
    },
    emailText: {
        css: infoTextCss,
    },
    phoneText: {
        css: infoTextCss,
    },
    emailIcon: {
        src: EmailLine,
        size: 18,
        css: iconCss,
    },
    phoneIcon: {
        src: PhoneLine,
        size: 18,
        css: iconCss,
    },
};
const styles = contactUsHelpStyles;

const ContactUsHelp: FC<ContactUsHelpProps> = ({
    fields: { accountsReceivableEmail, description, email, phoneNumber },
}) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper>
                {accountsReceivableEmail && (
                    <>
                        <Typography {...styles.title}>{translate("Accounts Receivable")}</Typography>
                        <Typography {...styles.emailText}>
                            <Icon {...styles.emailIcon} title={translate("Email")} />
                            <Link href={`mailto:${accountsReceivableEmail}`}>{accountsReceivableEmail}</Link>
                        </Typography>
                    </>
                )}
                <ContactUsSalesRepInfos fields={{ showIcons: true }} />
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                {description && <Typography {...styles.description}>{description}</Typography>}
                {email && (
                    <Typography {...styles.emailText}>
                        <Icon {...styles.emailIcon} title={translate("Email")} />
                        <Link href={`mailto:${email}`}>{email}</Link>
                    </Typography>
                )}
                {phoneNumber && (
                    <Typography {...styles.phoneText}>
                        <Icon {...styles.phoneIcon} title={translate("Phone")} />
                        <Link href={`tel:${phoneNumber}`}>{phoneNumber}</Link>
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContactUsHelp,
    definition: {
        displayName: "Help",
        group: "Contact Us",
        fieldDefinitions: [
            {
                name: fields.accountsReceivableEmail,
                displayName: "Accounts Receivable Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
